/**
 * Ultra-Optimized Mobile UI Component
 * Maximum content density with minimal vertical spacing
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useItems } from '../../contexts/ItemsContext';
import './MobileUIOptimized.css';

function MobileUIOptimized({ activeTab = 'passwords', onAddClick, onItemClick, onTabChange, onShare }) {
  const navigate = useNavigate();
  const { allItems = [], searchItems, updateFilters } = useItems();
  
  // Filter items by type
  const passwords = allItems.filter(item => item.itemType === 'password');
  const creditCards = allItems.filter(item => item.itemType === 'creditcard');
  const apiKeys = allItems.filter(item => item.itemType === 'apikey');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [currentTab, setCurrentTab] = useState(activeTab);
  const [showCategories, setShowCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortBy, setSortBy] = useState('recent'); // recent, name, favorite
  const searchInputRef = useRef(null);

  // Sync currentTab with activeTab prop changes
  useEffect(() => {
    setCurrentTab(activeTab);
  }, [activeTab]);

  // Get current data based on tab
  const getCurrentData = () => {
    switch (currentTab) {
      case 'creditcards':
        return creditCards;
      case 'apikeys':
        return apiKeys;
      case 'all':
        return allItems;
      case 'passwords':
      default:
        return passwords;
    }
  };

  const data = getCurrentData();

  // Get unique categories from data
  const getCategories = () => {
    const categories = new Set();
    data.forEach(item => {
      if (item.category) categories.add(item.category);
    });
    return Array.from(categories).sort();
  };

  const categories = getCategories();

  // Filter data based on active filter and category
  let filteredData = data.filter(item => {
    // Category filter first
    if (selectedCategory && item.category !== selectedCategory) return false;
    
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matches = (
        item.title?.toLowerCase().includes(searchLower) ||
        item.username?.toLowerCase().includes(searchLower) ||
        item.website?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.category?.toLowerCase().includes(searchLower)
      );
      if (!matches) return false;
    }
    
    // Badge filter for 전체/즐겨찾기
    if (activeFilter === 'favorite') {
      return item.isFavorite;
    }
    
    // 전체는 모든 아이템 표시
    return activeFilter === 'all';
  });

  // Sort the filtered data
  filteredData = [...filteredData].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return (a.title || '').localeCompare(b.title || '');
      case 'favorite':
        // Favorites first, then by name
        if (a.isFavorite === b.isFavorite) {
          return (a.title || '').localeCompare(b.title || '');
        }
        return b.isFavorite ? 1 : -1;
      case 'recent':
      default:
        // Most recently updated first
        return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
    }
  });

  // Stats calculation
  const stats = {
    total: data.length,
    secure: data.filter(d => d.isPinProtected).length,
    favorite: data.filter(d => d.isFavorite).length
  };

  // Search handler with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        updateFilters({ searchTerm });
      } else {
        updateFilters({ searchTerm: '' });
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm, updateFilters]);

  return (
    <div className="mobile-container">
      {/* Compact Search Header */}
      <div className="mobile-search-header">
        <div className="mobile-search-field">
          <span className="mobile-search-icon">🔍</span>
          <input
            ref={searchInputRef}
            type="text"
            className="mobile-search-input"
            placeholder={`검색...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
          />
        </div>
        <button
          className="mobile-sort-button"
          onClick={() => {
            const nextSort = sortBy === 'recent' ? 'name' : sortBy === 'name' ? 'favorite' : 'recent';
            setSortBy(nextSort);
          }}
          title={`정렬: ${sortBy === 'recent' ? '최근' : sortBy === 'name' ? '이름' : '즐겨찾기'}`}
        >
          {sortBy === 'recent' ? '🕐' : sortBy === 'name' ? '🔤' : '⭐'}
        </button>
      </div>

      {/* Filter Section */}
      <div className="mobile-filter-section">
        {/* Consolidated Filter Dropdown */}
        <div className="mobile-consolidated-dropdown">
          <select
            className="mobile-consolidated-select"
            value={activeFilter === 'favorite' && currentTab === 'all' ? 'all_favorite' : `${currentTab}_${activeFilter}`}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'all_favorite') {
                setCurrentTab('all');
                setActiveFilter('favorite');
                onTabChange && onTabChange('all');
              } else {
                const [tab, filter] = value.split('_');
                setCurrentTab(tab);
                setActiveFilter(filter);
                onTabChange && onTabChange(tab);
              }
            }}
          >
            <option value="passwords_all">🔑 패스워드 ({passwords.length})</option>
            <option value="creditcards_all">💳 신용카드 ({creditCards.length})</option>
            <option value="apikeys_all">🔐 API 키 ({apiKeys.length})</option>
            <option value="all_favorite">⭐ 즐겨찾기 ({allItems.filter(item => item.isFavorite).length})</option>
          </select>
        </div>
      </div>


      {/* Content List */}
      <div className="mobile-content-list">
        {filteredData.length === 0 ? (
          <div className="mobile-empty-minimal">
            <div className="mobile-empty-icon-mini">
              {searchTerm ? '🔍' : '📭'}
            </div>
            <p className="mobile-empty-text">
              {searchTerm ? '검색 결과가 없습니다' : '항목이 없습니다'}
            </p>
          </div>
        ) : (
          filteredData.map(item => (
            <MobileItemCard 
              key={item.id} 
              item={item} 
              type={currentTab === 'all' ? item.itemType : currentTab}
              onClick={() => onItemClick && onItemClick(item)}
              onShare={onShare}
            />
          ))
        )}
      </div>

      {/* Mini FAB */}
      <button className="mobile-fab-mini" onClick={onAddClick}>+</button>

      {/* Tab Bar - Extended with navigation */}
      <div className="mobile-tab-bar-extended">
        <div className="mobile-tab-row">
          <button
            className={`mobile-tab-item ${currentTab === 'passwords' ? 'active' : ''}`}
            onClick={() => {
              setCurrentTab('passwords');
              onTabChange && onTabChange('passwords');
            }}
          >
            <span className="mobile-tab-icon">🔑</span>
            <span className="mobile-tab-label">패스워드</span>
          </button>
          <button
            className={`mobile-tab-item ${currentTab === 'creditcards' ? 'active' : ''}`}
            onClick={() => {
              setCurrentTab('creditcards');
              onTabChange && onTabChange('creditcards');
            }}
          >
            <span className="mobile-tab-icon">💳</span>
            <span className="mobile-tab-label">카드</span>
          </button>
          <button
            className={`mobile-tab-item ${currentTab === 'apikeys' ? 'active' : ''}`}
            onClick={() => {
              setCurrentTab('apikeys');
              onTabChange && onTabChange('apikeys');
            }}
          >
            <span className="mobile-tab-icon">🔐</span>
            <span className="mobile-tab-label">API</span>
          </button>
          <button
            className="mobile-tab-item"
            onClick={() => navigate('/secure-notes')}
          >
            <span className="mobile-tab-icon">📝</span>
            <span className="mobile-tab-label">메모</span>
          </button>
          <button
            className="mobile-tab-item"
            onClick={() => navigate('/secure-files')}
          >
            <span className="mobile-tab-icon">📁</span>
            <span className="mobile-tab-label">파일</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Ultra-Compact Item Card
function MobileItemCard({ item, type, onClick, onShare }) {
  const getIcon = () => {
    if (type === 'creditcards' || item.itemType === 'creditcard') return '💳';
    if (type === 'apikeys' || item.itemType === 'apikey') return '🔐';
    return '🔑';
  };

  const getIconClass = () => {
    if (type === 'creditcards' || item.itemType === 'creditcard') return 'creditcard';
    if (type === 'apikeys' || item.itemType === 'apikey') return 'apikey';
    return 'password';
  };

  const getTitle = () => {
    if (item.title && item.title.trim()) return item.title;
    
    // For credit cards, try to build meaningful title
    if (type === 'creditcards' || item.itemType === 'creditcard') {
      if (item.cardName) return item.cardName;
      if (item.bankName) return item.bankName;
      if (item.cardNumber) {
        const lastFour = item.cardNumber.slice(-4);
        return `카드 **** ${lastFour}`;
      }
      return '신용카드';
    }
    
    // For API keys, try to build meaningful title
    if (type === 'apikeys' || item.itemType === 'apikey') {
      if (item.serviceName) return item.serviceName;
      if (item.apiName) return item.apiName;
      if (item.provider) return item.provider;
      if (item.keyName) return item.keyName;
      return 'API 키';
    }
    
    // For passwords
    if (item.website) return item.website;
    if (item.username) return item.username;
    
    return '제목 없음';
  };

  const getSubtitle = () => {
    if (item.username) return item.username;
    if (item.website) return item.website;
    if (item.description) {
      return item.description.length > 40 
        ? item.description.substring(0, 40) + '...' 
        : item.description;
    }
    if (item.fileSize) {
      const size = (item.fileSize / 1024).toFixed(1);
      return `${size} KB`;
    }
    return '';
  };

  return (
    <div className="mobile-item-card">
      <div className="mobile-item-row" onClick={onClick}>
        <div className={`mobile-item-icon ${getIconClass()}`}>
          {getIcon()}
        </div>
        
        <div className="mobile-item-content">
          <div className="mobile-item-header">
            <h3 className="mobile-item-title">{getTitle()}</h3>
            <div className="mobile-item-indicators">
              {item.isPinProtected && (
                <span className="mobile-indicator secure">🔒</span>
              )}
              {item.isFavorite && (
                <span className="mobile-indicator favorite">⭐</span>
              )}
            </div>
          </div>
          {getSubtitle() && (
            <p className="mobile-item-subtitle">{getSubtitle()}</p>
          )}
        </div>
        
        <div className="mobile-item-actions">
          <button
            className="mobile-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              onShare && onShare(item);
            }}
            title="공유하기"
          >
            📤
          </button>
        </div>
      </div>
    </div>
  );
}

export default MobileUIOptimized;